import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MobileTabBar } from '../components/MobileTabBar';
import { AppNavbar } from '../components/AppNavbar';
import {
  CalendarClock, Trash2, Pencil, CheckCircle, XCircle, Clock,
  Loader2, Instagram, Facebook, Film, Image, Circle, Send, AlertTriangle, RefreshCw,
  List as ListIcon, CalendarDays, ChevronLeft, ChevronRight, Images,
} from 'lucide-react';

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: Clock, dot: 'bg-blue-500', chip: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  publishing: { label: 'Publishing…', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Loader2, dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  published: { label: 'Published', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle, dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle, dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', color: 'text-gray-500 bg-gray-50 border-gray-200', icon: XCircle, dot: 'bg-gray-400', chip: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const PLATFORM_ICON = { instagram: Instagram, facebook: Facebook };
const TYPE_ICON = { reel: Film, post: Image, story: Circle, carousel: Images };

export default function ScheduledPostsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [editDate, setEditDate] = useState('');
  const [view, setView] = useState('list');
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [modalPost, setModalPost] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/social/scheduled');
      setPosts(res.data.scheduled_posts || []);
    } catch {
      toast.error('Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCancel = async (scheduleId) => {
    try {
      await apiClient.delete(`/social/scheduled/${scheduleId}`);
      toast.success('Scheduled post cancelled');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const startEdit = (post) => {
    setEditingId(post.schedule_id);
    setEditCaption(post.caption || '');
    setEditDate(post.scheduled_at ? post.scheduled_at.slice(0, 16) : '');
  };

  const saveEdit = async (scheduleId) => {
    try {
      const updates = {};
      if (editCaption) updates.caption = editCaption;
      if (editDate) updates.scheduled_at = new Date(editDate).toISOString();
      await apiClient.patch(`/social/scheduled/${scheduleId}`, updates);
      toast.success('Updated successfully');
      setEditingId(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const scheduled = posts.filter(p => p.status === 'scheduled');
  const past = posts.filter(p => p.status !== 'scheduled');

  const handleModalReschedule = async (scheduleId, newIsoDate, newCaption) => {
    try {
      const updates = {};
      if (newCaption != null) updates.caption = newCaption;
      if (newIsoDate) updates.scheduled_at = new Date(newIsoDate).toISOString();
      await apiClient.patch(`/social/scheduled/${scheduleId}`, updates);
      toast.success('Rescheduled');
      setModalPost(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const handleModalCancel = async (scheduleId) => {
    await handleCancel(scheduleId);
    setModalPost(null);
  };

  return (
    <div
      className="min-h-screen bg-white pb-28 md:pb-10"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="scheduled-posts-page"
    >
      {/* Unified navigation */}
      <AppNavbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Hero */}
        <section className="pt-6 sm:pt-8 pb-2 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Queue</p>
            <h2
              className="mt-1 text-3xl sm:text-4xl font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Your{' '}
              <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>schedule</span>.
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-400">
              {scheduled.length} upcoming · {past.length} completed
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* View toggle */}
            <div className="inline-flex rounded-full border border-gray-200 p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setView('list')}
                className={`h-8 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === 'list' ? 'bg-black text-white' : 'text-gray-500 hover:text-gray-800'}`}
                data-testid="view-list-btn"
              >
                <ListIcon className="h-3.5 w-3.5" /> List
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`h-8 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === 'calendar' ? 'bg-black text-white' : 'text-gray-500 hover:text-gray-800'}`}
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="h-3.5 w-3.5" /> Calendar
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPosts}
              disabled={loading}
              className="rounded-full"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : view === 'calendar' ? (
          <CalendarView
            posts={posts}
            month={calMonth}
            onPrevMonth={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            onNextMonth={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            onToday={() => { const d = new Date(); setCalMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}
            onSelectPost={setModalPost}
          />
        ) : posts.length === 0 ? (
          <div className="text-center py-16 sm:py-20 px-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center mx-auto mb-5">
              <CalendarClock className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Nothing scheduled
            </p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1 max-w-[280px] mx-auto">
              Schedule a post from the Reel Editor or any Campaign editor and it'll appear here.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {/* Upcoming */}
            {scheduled.length > 0 && (
              <section>
                <h3
                  className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Upcoming
                </h3>
                <div className="space-y-3">
                  {scheduled.map(post => (
                    <PostCard
                      key={post.schedule_id}
                      post={post}
                      isEditing={editingId === post.schedule_id}
                      editCaption={editCaption}
                      editDate={editDate}
                      onEditCaption={setEditCaption}
                      onEditDate={setEditDate}
                      onStartEdit={() => startEdit(post)}
                      onSaveEdit={() => saveEdit(post.schedule_id)}
                      onCancelEdit={() => setEditingId(null)}
                      onCancel={() => handleCancel(post.schedule_id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* History */}
            {past.length > 0 && (
              <section>
                <h3
                  className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-semibold mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  History
                </h3>
                <div className="space-y-3">
                  {past.map(post => (
                    <PostCard key={post.schedule_id} post={post} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <PostDetailModal
        post={modalPost}
        onClose={() => setModalPost(null)}
        onReschedule={handleModalReschedule}
        onCancel={handleModalCancel}
      />

      <MobileTabBar />
    </div>
  );
}

// ── Month calendar view ──
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function CalendarView({ posts, month, onPrevMonth, onNextMonth, onToday, onSelectPost }) {
  // Group posts by local day
  const byDay = {};
  posts.forEach(p => {
    if (!p.scheduled_at) return;
    const d = new Date(p.scheduled_at);
    const k = dateKey(d);
    (byDay[k] = byDay[k] || []).push(p);
  });
  Object.values(byDay).forEach(arr => arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));

  const year = month.getFullYear();
  const mon = month.getMonth();
  const firstWeekday = new Date(year, mon, 1).getDay();
  const daysInMonth = new Date(year, mon + 1, 0).getDate();
  const todayKey = dateKey(new Date());

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, mon, day));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="mt-6" data-testid="calendar-view">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {month.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h3>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="rounded-full h-8" onClick={onToday} data-testid="cal-today-btn">Today</Button>
          <button onClick={onPrevMonth} className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="cal-prev-btn" aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={onNextMonth} className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="cal-next-btn" aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1">
        {WEEKDAYS.map(w => (
          <div key={w} className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold text-center py-1">{w}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`b${idx}`} className="min-h-[70px] sm:min-h-[96px] rounded-xl bg-gray-50/50" />;
          const k = dateKey(cell);
          const dayPosts = byDay[k] || [];
          const isToday = k === todayKey;
          return (
            <div
              key={k}
              className={`min-h-[70px] sm:min-h-[96px] rounded-xl border p-1 sm:p-1.5 flex flex-col gap-1 ${isToday ? 'border-black/40 bg-gray-50' : 'border-gray-100 bg-white'}`}
              data-testid={`cal-day-${k}`}
            >
              <span className={`text-[11px] font-semibold ${isToday ? 'text-black' : 'text-gray-400'} px-0.5`}>{cell.getDate()}</span>
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {dayPosts.slice(0, 3).map(p => {
                  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.scheduled;
                  const TypeIcon = TYPE_ICON[p.content_type] || Film;
                  const t = new Date(p.scheduled_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                  return (
                    <button
                      key={p.schedule_id}
                      onClick={() => onSelectPost(p)}
                      className={`w-full text-left text-[10px] sm:text-[11px] rounded-md border px-1 sm:px-1.5 py-0.5 inline-flex items-center gap-1 truncate transition-colors ${cfg.chip}`}
                      data-testid={`cal-chip-${p.schedule_id}`}
                      title={`${t} · ${p.account_name || ''} · ${p.content_type}`}
                    >
                      <TypeIcon className="h-2.5 w-2.5 flex-shrink-0" />
                      <span className="truncate">{t} {p.account_name || p.content_type}</span>
                    </button>
                  );
                })}
                {dayPosts.length > 3 && (
                  <button
                    onClick={() => onSelectPost(dayPosts[3])}
                    className="text-[10px] text-gray-400 hover:text-gray-700 px-1 text-left"
                    data-testid={`cal-more-${k}`}
                  >
                    +{dayPosts.length - 3} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        {['scheduled', 'published', 'failed'].map(s => (
          <span key={s} className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
            <span className={`h-2 w-2 rounded-full ${STATUS_CONFIG[s].dot}`} /> {STATUS_CONFIG[s].label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Reschedule / cancel modal (opened from a calendar chip) ──
function PostDetailModal({ post, onClose, onReschedule, onCancel }) {
  const [caption, setCaption] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    if (post) {
      setCaption(post.caption || '');
      setDate(post.scheduled_at ? post.scheduled_at.slice(0, 16) : '');
    }
  }, [post]);

  if (!post) return null;
  const cfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;
  const StatusIcon = cfg.icon;
  const PlatformIcon = PLATFORM_ICON[post.platform] || Send;
  const TypeIcon = TYPE_ICON[post.content_type] || Film;
  const editable = post.status === 'scheduled';

  return (
    <Dialog open={!!post} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="cal-post-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PlatformIcon className={`h-4 w-4 ${post.platform === 'instagram' ? 'text-pink-500' : 'text-blue-600'}`} />
            {post.account_name || 'Account'}
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${cfg.color}`}>
              <StatusIcon className={`h-2.5 w-2.5 ${post.status === 'publishing' ? 'animate-spin' : ''}`} /> {cfg.label}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[11px] text-gray-400 capitalize">
            <TypeIcon className="h-3 w-3" /> {post.content_type}
          </div>

          {editable ? (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Caption</label>
                <Textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3} className="text-sm rounded-xl" data-testid="modal-caption-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Date & time</label>
                <Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} className="text-sm h-11 rounded-xl" data-testid="modal-date-input" />
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700 leading-snug">{post.caption || 'No caption'}</p>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Clock className="h-3 w-3" /> {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : '—'}
              </div>
              {post.error && (
                <div className="text-xs text-red-500 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {post.error}</div>
              )}
            </>
          )}
        </div>

        {editable && (
          <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
            <Button
              variant="ghost"
              className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => onCancel(post.schedule_id)}
              data-testid="modal-cancel-post-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Cancel post
            </Button>
            <Button
              className="rounded-full bg-black hover:bg-gray-900 text-white"
              onClick={() => onReschedule(post.schedule_id, date, caption)}
              data-testid="modal-reschedule-btn"
            >
              <Pencil className="h-4 w-4 mr-1.5" /> Reschedule
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PostCard({ post, isEditing, editCaption, editDate, onEditCaption, onEditDate, onStartEdit, onSaveEdit, onCancelEdit, onCancel }) {
  const statusCfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.scheduled;
  const StatusIcon = statusCfg.icon;
  const PlatformIcon = PLATFORM_ICON[post.platform] || Send;
  const TypeIcon = TYPE_ICON[post.content_type] || Film;
  const scheduledDt = post.scheduled_at ? new Date(post.scheduled_at) : null;
  const isUpcoming = post.status === 'scheduled';

  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 transition-all ${
        isEditing
          ? 'border-black/30 bg-gray-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      data-testid={`scheduled-post-${post.schedule_id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Platform icon */}
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
            <PlatformIcon
              className={`h-5 w-5 ${
                post.platform === 'instagram' ? 'text-pink-500' : 'text-blue-600'
              }`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold tracking-tight truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {post.account_name || 'Account'}
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${statusCfg.color}`}
            >
              <StatusIcon className={`h-2.5 w-2.5 ${post.status === 'publishing' ? 'animate-spin' : ''}`} />
              {statusCfg.label}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 capitalize">
              <TypeIcon className="h-3 w-3" />
              {post.content_type}
            </span>
            {post.brand_voice && (
              <span className="text-[11px] text-gray-300">· {post.brand_voice}</span>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-2 pt-1">
              <Textarea
                value={editCaption}
                onChange={e => onEditCaption(e.target.value)}
                rows={3}
                className="text-sm rounded-xl"
                data-testid="edit-caption-input"
              />
              <Input
                type="datetime-local"
                value={editDate}
                onChange={e => onEditDate(e.target.value)}
                className="text-sm h-11 rounded-xl"
                data-testid="edit-date-input"
              />
              <div className="flex gap-2 pt-1">
                <Button
                  className="h-10 px-5 rounded-full bg-black hover:bg-gray-900 text-white text-sm"
                  onClick={onSaveEdit}
                  data-testid="save-edit-btn"
                >
                  Save changes
                </Button>
                <Button
                  variant="ghost"
                  className="h-10 px-5 rounded-full text-sm"
                  onClick={onCancelEdit}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700 line-clamp-2 leading-snug">{post.caption || 'No caption'}</p>
              <div className="flex items-center gap-2 text-[11px] sm:text-xs text-gray-400 flex-wrap">
                <Clock className="h-3 w-3" />
                <span>{scheduledDt ? scheduledDt.toLocaleString() : '—'}</span>
                {post.error && (
                  <span className="text-red-500 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {post.error}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        {isUpcoming && !isEditing && (
          <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
            <button
              onClick={onStartEdit}
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
              title="Edit"
              data-testid={`edit-btn-${post.schedule_id}`}
              aria-label="Edit scheduled post"
            >
              <Pencil className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={onCancel}
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-red-50 active:scale-95 transition-all"
              title="Cancel"
              data-testid={`cancel-btn-${post.schedule_id}`}
              aria-label="Cancel scheduled post"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
